import { create, useStore } from 'zustand';

export enum ApplicationDataTabType {
  ORIGINAL,
  DRAFT,
  REQUEST,
  AXO_VERSION,
}

export enum ApplicationDataTabPage {
  DATA,
  LENDERS,
}

export type ApplicationDataTab =
  | {
      type: ApplicationDataTabType.ORIGINAL;
      currentPage: ApplicationDataTabPage;
    }
  | {
      type: ApplicationDataTabType.REQUEST;
      requestID: string;
    }
  | {
      type: ApplicationDataTabType.DRAFT;
      sourceRequestID?: string;
      currentPage: ApplicationDataTabPage;
    }
  | {
      type: ApplicationDataTabType.AXO_VERSION;
      currentPage: ApplicationDataTabPage;
    };

interface ApplicationDataState {
  selectedTab: ApplicationDataTab;
  selectedLenderID?: string;
  selectRequest(args: { requestID: string; lenderID: string }): void;
  selectOriginal(): void;
  selectAxoVersion(): void;

  isEditing: boolean;
  setIsEditing(isEditing: boolean): void;
  createDraft(): void;
  sendToBanks(): void;
  goToPreviousPage(): void;
}

const createApplicationDataStore = () =>
  create<ApplicationDataState>((set) => ({
    selectedTab: {
      type: ApplicationDataTabType.ORIGINAL,
      currentPage: ApplicationDataTabPage.DATA,
    },
    selectedLenderID: undefined,
    selectRequest({ requestID, lenderID }) {
      set({
        selectedTab: { type: ApplicationDataTabType.REQUEST, requestID },
        selectedLenderID: lenderID,
        isEditing: false,
      });
    },
    selectOriginal() {
      set({
        selectedTab: {
          type: ApplicationDataTabType.ORIGINAL,
          currentPage: ApplicationDataTabPage.DATA,
        },
        isEditing: false,
      });
    },
    selectAxoVersion() {
      set({
        selectedTab: {
          type: ApplicationDataTabType.AXO_VERSION,
          currentPage: ApplicationDataTabPage.DATA,
        },
        isEditing: false,
      });
    },
    lastUpdated: undefined,
    isEditing: false,
    setIsEditing(isEditing) {
      set((state) => {
        let selectedTab: ApplicationDataTab;
        if (
          isEditing === false &&
          state.selectedTab.type === ApplicationDataTabType.DRAFT
        ) {
          if (state.selectedTab.sourceRequestID) {
            selectedTab = {
              type: ApplicationDataTabType.REQUEST,
              requestID: state.selectedTab.sourceRequestID,
            };
          } else {
            selectedTab = {
              type: ApplicationDataTabType.ORIGINAL,
              currentPage: ApplicationDataTabPage.DATA,
            };
          }
        } else if (
          isEditing === false &&
          state.selectedTab.type === ApplicationDataTabType.AXO_VERSION
        ) {
          selectedTab = {
            ...state.selectedTab,
            currentPage: ApplicationDataTabPage.DATA,
          };
        } else {
          selectedTab = state.selectedTab;
        }

        return {
          isEditing,
          selectedTab,
        };
      });
    },
    sendToBanks() {
      set((state) => {
        if (state.selectedTab.type === ApplicationDataTabType.DRAFT)
          return {
            selectedTab: {
              ...state.selectedTab,
              currentPage: ApplicationDataTabPage.LENDERS,
            },
          };

        if (
          state.selectedTab.type === ApplicationDataTabType.AXO_VERSION ||
          state.selectedTab.type === ApplicationDataTabType.ORIGINAL
        )
          return {
            isEditing: true,
            selectedTab: {
              ...state.selectedTab,
              currentPage: ApplicationDataTabPage.LENDERS,
            },
          };

        return {};
      });
    },
    createDraft() {
      set((state) => ({
        selectedTab: {
          type: ApplicationDataTabType.DRAFT,
          sourceRequestID:
            state.selectedTab.type === ApplicationDataTabType.REQUEST
              ? state.selectedTab.requestID
              : undefined,
          currentPage: ApplicationDataTabPage.DATA,
        },
        isEditing: true,
      }));
    },
    goToPreviousPage() {
      set((state) => {
        if (
          state.selectedTab.type === ApplicationDataTabType.DRAFT ||
          state.selectedTab.type === ApplicationDataTabType.AXO_VERSION ||
          state.selectedTab.type === ApplicationDataTabType.ORIGINAL
        )
          return {
            selectedTab: {
              ...state.selectedTab,
              currentPage: ApplicationDataTabPage.DATA,
            },
          };

        return {};
      });
    },
  }));

const applicationDataStores: Record<
  string,
  ReturnType<typeof createApplicationDataStore>
> = {};

export function getApplicationDataStore(applicationID: string) {
  if (!applicationDataStores[applicationID])
    applicationDataStores[applicationID] = createApplicationDataStore();

  return applicationDataStores[applicationID];
}

export function useApplicationDataStore(applicationID: string) {
  return useStore(getApplicationDataStore(applicationID));
}
